import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { callApi } from "../../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../../ApiHelper/ApiUrl";
import SearchIcon from "../../../../components/Icons/SearchIcon";
import BgBlurLoader from "../../../../components/Loaders/BgBlurLoader";
import MainHeading from "../../../../components/comman/MainHeading";
import { updateFiltersStates } from "../../../../store/actions/UserActions";
import SearchSiteSelect from "../../../../components/comman/SiteSearchSelect";

function SiteSalaryConfig() {
  const userDetails = useSelector((state) => state.root.userDetails);
  const filterDetails = useSelector((state) => state.root.filtersStates);
  const searchTimeout = useRef(null);
  const dispatch = useDispatch();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loadingSave, setLoadingSave] = useState(false);
  const [isCleared, setIsCleared] = useState(false);

  const [pageStates, setPageStates] = useState({
    siteList: [],
    loadingEmployees: false,
    siteId: filterDetails?.siteId,
    isLoadingSite: false,
    isInitial: true,
  });
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    GetSiteList();
  }, []);

  const GetSiteList = (
    companyId = null,
    searchQuery = null,
    autoSelect = true
  ) => {
    return new Promise((resolve) => {
      let API =
        userDetails?.type === 2
          ? apiUrl.get_site +
            `?pageSize=${100}${companyId ? `&companyId=${companyId}` : ""}${
              searchQuery ? `&search=${searchQuery}` : ""
            }`
          : apiUrl.get_user_site +
            `?pageSize=${100}${companyId ? `&companyId=${companyId}` : ""}${
              searchQuery ? `&search=${searchQuery}` : ""
            }`;

      setPageStates((prev) => ({
        ...prev,
        isLoadingSite: true,
      }));

      callApi(API, { method: "GET" })
        .then((response) => {
          setPageStates((prev) => ({
            ...prev,
            isLoadingSite: false,
          }));

          if (response?.status) {
            const formattedData = response.data?.site?.map((item) => ({
              value: item?.site?.id || item.id,
              label: item?.site?.name || item.name,
            }));

            const firstSiteId = formattedData?.[0]?.value || null;

            const selectedSiteId =
              filterDetails?.siteId &&
              formattedData.some((s) => s.value == filterDetails?.siteId)
                ? filterDetails?.siteId
                : firstSiteId;

            setPageStates((prev) => ({
              ...prev,
              siteList: formattedData,
              siteId: autoSelect ? selectedSiteId : null,
              isInitial: false,
            }));

            if (autoSelect && selectedSiteId) {
              handleSiteChange({ siteId: selectedSiteId }, "site");
            }

            resolve(formattedData);
          } else {
            resolve([]);
          }
        })
        .catch(() => {
          setPageStates((prev) => ({
            ...prev,
            isLoadingSite: false,
          }));
          resolve([]);
        });
    });
  };

  const handleSiteChange = async (site, type) => {
    // console.log("site:--------->", site);
    setPageStates((prevState) => ({
      ...prevState,
      ...(type === "site" && { siteId: site.siteId }),
    }));
    dispatch(
      updateFiltersStates({
        ...(type === "site" && { siteId: site.siteId }),
      })
    );

    if (!site?.siteId) {
      setSelectedSiteId(null);
      setEmployees([]);
      return;
    }

    setSelectedSiteId(site.siteId);
    setPageStates((p) => ({ ...p, loadingEmployees: true }));

    try {
      const API = `${apiUrl.get_site_wise_user_salary}?siteId=${site.siteId}`;
      const response = await callApi(API, { method: "GET" });

      if (response?.status) {
        if (response?.data?.length < 0) return;
        const list = response?.data?.map((u, idx) => {
          const userId = u?.id;
          if (u?.usersalaries.length < 0) return;

          let dailyWagesObj = null;
          let grossSalaryObj = null;

          for (const s of u?.usersalaries || []) {
            const name = s?.sitesalaryhead?.salaryhead?.name?.toLowerCase();

            if (name === "daily wages") {
              dailyWagesObj = s;
            } else if (name === "gross salary") {
              grossSalaryObj = s;
            }

            if (dailyWagesObj && grossSalaryObj) break;
          }

          return {
            _key: userId ? `emp-${userId}` : `temp-${idx}`,
            id: userId,
            userId: userId,
            employeeCode: u?.employeeCode,
            name: u?.name,
            dailyWages: dailyWagesObj?.amount ?? "",
            grossAmount: grossSalaryObj?.amount ?? "",
            dailyWagesHeadId: dailyWagesObj?.sitesalaryhead?.id ?? null,
            grossSalaryHeadId: grossSalaryObj?.sitesalaryhead?.id ?? null,
          };
        });
        setEmployees(list);
        setFilteredEmployees(list);
      } else {
        setEmployees([]);
        toast.error("Failed to fetch employees for site");
      }
    } catch {
      toast.error("Error fetching employee data");
      setEmployees([]);
    } finally {
      setTimeout(() => {
        setPageStates((p) => ({ ...p, loadingEmployees: false }));
      }, 200);
    }
  };

  const handleInputChange = (key, field, value) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp._key === key ? { ...emp, [field]: value || "" } : emp
      )
    );
    setFilteredEmployees((prev) =>
      prev.map((emp) =>
        emp._key === key ? { ...emp, [field]: value || "" } : emp
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedSiteId) {
      toast.error("Please select a site first");
      return;
    }

    const invalid = employees.some(
      (e) =>
        !e.dailyWages ||
        !e.grossAmount ||
        e.dailyWages === "" ||
        e.grossAmount === ""
    );

    if (invalid) {
      toast.error("Please fill all salary fields before saving");
      return;
    }

    const payload = {
      salary: employees.map((e) => ({
        userId: e?.userId,
        userSalary: [
          {
            amount: String(e?.dailyWages),
            siteSalaryHeadId: e?.dailyWagesHeadId,
          },
          {
            amount: String(e?.grossAmount),
            siteSalaryHeadId: e?.grossSalaryHeadId,
          },
        ],
      })),
    };

    const API = `account/update-site-wise-user-salary?siteId=${selectedSiteId}`;

    setLoadingSave(true);
    try {
      const res = await callApi(API, { method: "POST", data: payload });
      if (res?.status) {
        setHasChanges(false);
        toast.success("Employee salary data saved successfully!");
      } else {
        toast.error(res?.message || "Failed to save data");
      }
    } catch (err) {
      toast.error("Error saving data");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase().trim();
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      if (!searchTerm) {
        setFilteredEmployees(employees);
      } else {
        setFilteredEmployees(
          employees.filter(
            (emp) =>
              emp.name?.toLowerCase().includes(searchTerm) ||
              emp.employeeCode?.toLowerCase().includes(searchTerm)
          )
        );
      }
    }, 200);
  };

  const renderTable = () => {
    if (!selectedSiteId)
      return (
        <tr>
          <td colSpan="5" className="text-center text-muted py-4">
            Please select a site to view employees
          </td>
        </tr>
      );

    if (pageStates.loadingEmployees)
      return (
        <tr>
          <td colSpan="5" className="text-center text-muted py-4">
            Loading employees...
          </td>
        </tr>
      );

    if (filteredEmployees.length === 0)
      return (
        <tr>
          <td colSpan="5" className="text-center text-muted py-4">
            No employees found for this site
          </td>
        </tr>
      );

    return filteredEmployees.map((emp, i) => (
      <tr key={emp._key}>
        <td className="text-start">{String(i + 1).padStart(2, "0")}</td>
        <td className="text-start">{emp.employeeCode}</td>
        <td className="text-start">{emp.name}</td>
        <td style={{ width: "200px" }}>
          <input
            type="number"
            onWheel={(e) => e.target.blur()}
            className="form-control"
            value={emp.dailyWages}
            onChange={(e) =>
              handleInputChange(emp._key, "dailyWages", e.target.value)
            }
            min="0"
            step="0.01"
            placeholder="Daily Wages"
          />
        </td>
        <td style={{ width: "200px" }}>
          <input
            type="number"
            onWheel={(e) => e.target.blur()}
            className="form-control"
            value={emp.grossAmount}
            onChange={(e) =>
              handleInputChange(emp._key, "grossAmount", e.target.value)
            }
            min="0"
            step="0.01"
            placeholder="Gross Amount"
          />
        </td>
      </tr>
    ));
  };

  const sortList = {
    site: pageStates?.siteList,
    isShow: "allNotshow",
  };

  return (
    <>
      {(pageStates?.isLoadingSite ||
        pageStates?.loadingEmployees ||
        loadingSave) && <BgBlurLoader />}

      <div className="content">
        <MainHeading title="Site Salary Config" />
        <div className="card">
          <div className="card-body">
            <div className="table-head d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
              <div className="position-relative">
                <span className="search-icon">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  className="form-control search-control"
                  id="searchUsers"
                  placeholder={"Search by name"}
                  onChange={handleSearch}
                />
              </div>
              <div className="d-flex gap-3 align-items-center">
                <SearchSiteSelect
                  style={{ minWidth: "25rem" }}
                  value={
                    sortList?.site?.find(
                      (item) => item?.value === pageStates?.siteId
                    )?.value
                  }
                  options={sortList?.site}
                  onSelect={(value) => {
                    setIsCleared(false);
                    handleSiteChange(
                      {
                        siteId: value?.value,
                      },
                      "site"
                    );
                  }}
                  onSearch={(input) => {
                    if (!input?.trim()) {
                      setIsCleared(true);
                      setSelectedSiteId(null);
                      setPageStates((prev) => ({ ...prev, siteId: null }));
                      setEmployees([]);
                      setFilteredEmployees([]);
                      dispatch(updateFiltersStates({ siteId: "" }));

                      GetSiteList(
                        filterDetails?.companyId ||
                          pageStates?.companyId ||
                          null,
                        null,
                        false
                      );
                      return;
                    }

                    const autoSelect = !isCleared;

                    GetSiteList(
                      filterDetails?.companyId || pageStates?.companyId || null,
                      input,
                      autoSelect
                    );
                  }}
                  isLoading={pageStates?.isLoadingSite}
                  placeholder={"Select Site"}
                  showClearButton={false}
                />
                <button
                  className="btn btn-primary w-100 fs-4 fw-bold text-white"
                  type="button"
                  disabled={!hasChanges}
                  onClick={handleSave}
                >
                  <i className="fas fa-save me-2"></i>Save
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-border-bottom align-middle nowrap w-100">
                <thead className="table-light">
                  <tr>
                    <th className="text-start">S.No</th>
                    <th className="text-start">Employee Code</th>
                    <th className="text-start">Employee Name</th>
                    <th className="text-start">Daily Wages (₹)</th>
                    <th className="text-start">Gross Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>{renderTable()}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SiteSalaryConfig;
